/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { Button } from '@mui/material';
import { CommonBorder } from 'ui/theme/Color';
import useSessionStore from 'stores/useSessionStore';
import { Logo } from './components/Logo';
import {
  Avatar,
  AvatarDiv,
  AvatarMenu,
  SettingMenu,
  AvatarCon,
  LayerBtnGroup,
} from './components/HeadLinkList/Avatar';
import { HeadLinkList } from './HeadLinkList';
import { getUserInfo } from 'apis/common/Employee';
import { devLogout } from 'apis/Session';
import { CloseBtn } from 'components/buttons/CustomButton';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { ComboBox } from 'components/selects/ComboBox';
import useLanguageStore from 'stores/useLanguageStore';
import { LangType } from 'models/common/Session';
import { SVGIcon } from 'components/buttons/IconSVG';
import { CommonText } from 'ui/theme/Color';
import { SetupProject } from 'models/setup/SetupProject';
import { findAssignedProject } from 'apis/setup/SetupProject';

/**
 * @desc HeadMenuBar
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.12.08   jshwang12     사용자 설정 > FA 프로젝트 추가 (FA 관련 권한이 있는 경우에만 노출)
 *                            협력사인 경우 프로젝트 설정을 제외한 사용자 설정 미노출
 */
const HeadMenuBar = () => {
  const {
    userId,
    empNm,
    langCd,
    jtiNm,
    deptNm,
    tmzFrmtCd,
    gptTmzCd,
    prdnProcCd,
    elmCopCd,
    mpClsfType,
    elmPrjId,
    roleCodes,
    ensolYn,
    resetSession,
  } = useSessionStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState('');
  const [isOpenAvatarMenu, setOpenAvatarMenu] = useState<boolean>(false);
  const [isOpenSettingMenu, setOpenSettingMenu] = useState<boolean>(false);

  const [timeZoneCode, setTimeZoneCode] = useState<Code[]>([]);
  const [empInfoCode, setEmpInfoCode] = useState<Code[]>([]);
  const [languageCode, setLanguageCode] = useState<Code[]>([]);
  const [timeFormatCode, setTimeFormatCode] = useState<Code[]>([]);
  const [prdnProcCode, setPrdnProcCode] = useState<Code[]>([]);
  const [elmCopCode, setElmCopCode] = useState<Code[]>([]);
  const [mpClsfTypeCode, setMpClsfTypeCode] = useState<Code[]>([]);
  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]);

  const [errors, setErrors] = useState<any>({});
  const { changeLanguage } = useLanguageStore();
  useEffect(() => {
    const init = async () => {
      if (userId) {
        setUserInfo(await getUserInfo());
      }
    };
    init();
    if (userId && languageCode.length == 0) {
      getCommonCodes();
    }
  });

  useEffect(() => {
    if (navigator.userAgent.toLowerCase().indexOf('edge') != -1) {
      document.addEventListener('mousedown', handleDetectsClick);
    } else {
      document.addEventListener('touchstart', handleDetectsClick);
    }
    return () => {
      if (navigator.userAgent.toLowerCase().indexOf('edge') != -1) {
        document.removeEventListener('mousedown', handleDetectsClick);
      } else {
        document.removeEventListener('touchstart', handleDetectsClick);
      }
    };
  }, []);

  const getCommonCodes = async () => {
    setLanguageCode((await getCommonCodeNames('LANG_CD')) ?? []);
    setEmpInfoCode((await getCommonCodeNames('ELM_EMP_INFO')) ?? []);
    setTimeZoneCode((await getCommonCodeNames('GPT_TMZ_CD')) ?? []);
    setTimeFormatCode((await getCommonCodeNames('TMZ_FRMT_CD')) ?? []);
    setPrdnProcCode((await getCommonCodeNames('PRDN_PROC_CD')) ?? []);
    setElmCopCode((await getCommonCodeNames('ELM_COP_CD')) ?? []);
    setMpClsfTypeCode((await getCommonCodeNames('MP_CLSF_CD')) ?? []);
  };

  const isDev = useMemo(() => {
    const appEnv = `${process.env.REACT_APP_NODE_ENV}`;
    return appEnv == 'local' || appEnv == 'dev' || appEnv == 'stg';
  }, []);

  const hasFaRole = useMemo(() => {
    return (roleCodes || []).filter((r) => r && r.startsWith('FA_')).length > 0;
  }, [roleCodes]);

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.elmPrjId,
          cmnCdNm: cur.elmPrjNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [prjMasterData]);

  useEffect(() => {
    if (isOpenSettingMenu && hasFaRole) {
      findAssignedProject().then((result: SetupProject[]) => {
        setPrjMasterData(result);
      });
    }
  }, [isOpenSettingMenu, hasFaRole]);

  const handleLogout = async () => {
    if (isDev || ensolYn === 'N') {
      const response = await devLogout();
      if (response.successOrNot === 'Y') {
        resetSession();
      }
      navigate('/system/login', { replace: true });
      window.location.reload();
      //window.close();
    }
    setOpenAvatarMenu(false);
  };

  const handleOpenAvatarMenu = () => {
    if (isDev || ensolYn === 'N') {
      setOpenSettingMenu(false);
      setOpenAvatarMenu(true);
    }
  };

  const handleMoveUserEditPage = () => {
    if (userId.includes('@') && userId.includes('.')) {
      navigate('/system/user-edit', {
        replace: true,
        state: {
          mnuNm: t('mp.label.협력업체 사용자 정보 수정', '협력업체 사용자 정보 수정'),
        },
      });
    } else {
      return false;
    }
  };

  const handleOpenSettingPopup = () => {
    setOpenAvatarMenu(false);
    setOpenSettingMenu(true);
  };

  const handleChangeLang = (value) => {
    changeLanguage(value as LangType);
    navigate('/', { replace: true });
    window.location.reload();
  };

  const handleChangeCombo = (value) => {
    for (let i = 0; i < empInfoCode.length; i++) {
      const combo = document.getElementsByClassName(empInfoCode[i].cmnCd!)[0] as HTMLElement;
      (combo.parentElement as HTMLElement).style.display = 'none';
    }
    const selCombo = document.getElementsByClassName(value)[0] as HTMLElement;
    (selCombo.parentElement as HTMLElement).style.display = 'block';
  };

  const handleChangeTMZ = (value) => {
    useSessionStore.getState().setGptTmzCd(value);
  };

  const handleChangeProc = (value) => {
    useSessionStore.getState().setPrdnProcCd(value);
  };

  const handleChangeFrmt = (value) => {
    useSessionStore.getState().setTmzFrmtCd(value);
  };

  const handleChangeElmCop = (value) => {
    useSessionStore.getState().setElmCopCd(value);
  };

  const handleChangeMpClsfType = (value) => {
    useSessionStore.getState().setMpClsfType(value);
  };

  const handleChangeElmPrjId = (value) => {
    useSessionStore.getState().setElmPrjId(value);
  };

  const handleSettingApply = async () => {
    window.location.reload();
  };

  /**
   * 외부 클릭 감지 이벤트
   * @param e
   */
  const handleDetectsClick = (e) => {
    let isSettingBox = false;
    let isAvatarBox = false;
    if (e.path && e.path !== undefined) {
      e.path.forEach((item) => {
        if (typeof item.className === 'string') {
          if (item.className?.includes('SettingBox')) {
            isSettingBox = true;
          }
          if (item.className?.includes('AvatarBox')) {
            isAvatarBox = true;
          }
        }
      });
    }

    if (isSettingBox) {
      setOpenSettingMenu(true);
    } else {
      setOpenSettingMenu(false);
    }

    if (isAvatarBox) {
      setOpenAvatarMenu(true);
    } else {
      setOpenAvatarMenu(false);
    }
  };

  const existsUserSettingMenu = useMemo(() => {
    if (userId && (empInfoCode || []).length > 0) {
      if (ensolYn === 'Y') {
        return true;
      } else {
        // 협력사인 경우 프로젝트를 제외한 사용자설정 미노출
        // 프로젝트는 FA 권한을 가진 경우에만 노출
        return hasFaRole;
      }
    }
    return false;
  }, [userId, ensolYn, hasFaRole, empInfoCode]);

  return (
    <div css={st.root(isDev, process.env.REACT_APP_NODE_ENV)}>
      <div css={st.inner}>
        <Logo />
      </div>
      <div>
        <HeadLinkList />
      </div>
      <div css={st.right}>
        {userId && (
          <ComboBox
            options={languageCode}
            defaultValue={langCd}
            isWrap={false}
            isError={errors?.languageCode}
            msgError={'선택해 주세요.'}
            className="language"
            onChange={(value) => handleChangeLang(value)}
            // placeholder={String(t('com.label.전체', '전체'))}
          />
        )}
        {existsUserSettingMenu && <Button css={st.btn} onClick={handleOpenSettingPopup}></Button>}
        <div className="SettingBox" css={SettingMenu.div(isOpenSettingMenu)}>
          <div className="SettingPopup">
            {/* // [25.12.08] 협력사인 경우 프로젝트 설정을 제외한 사용자 설정 미노출 */}
            {ensolYn === 'Y' && (
              <>
                {empInfoCode.filter((o) => o.cmnCd === 'GPT_TMZ_CD')[0]?.cmnCdNm && (
                  <div className="select">
                    <label>{empInfoCode.filter((o) => o.cmnCd === 'GPT_TMZ_CD')[0]?.cmnCdNm}</label>
                    <ComboBox
                      options={timeZoneCode}
                      defaultValue={gptTmzCd}
                      className="GPT_TMZ_CD"
                      isWrap={false}
                      onChange={(value) => handleChangeTMZ(value)}
                      placeholder={String(t('com.label.전체', '전체'))}
                    />
                  </div>
                )}
                {empInfoCode.filter((o) => o.cmnCd === 'TMZ_FRMT_CD')[0]?.cmnCdNm && (
                  <div className="select">
                    <label>
                      {empInfoCode.filter((o) => o.cmnCd === 'TMZ_FRMT_CD')[0]?.cmnCdNm}
                    </label>
                    <ComboBox
                      options={timeFormatCode}
                      defaultValue={tmzFrmtCd}
                      className="TMZ_FRMT_CD"
                      isWrap={false}
                      onChange={(value) => handleChangeFrmt(value)}
                      placeholder={String(t('com.label.전체', '전체'))}
                    />
                  </div>
                )}
                {/* 공정 */}
                {empInfoCode.filter((o) => o.cmnCd === 'PRDN_PROC_CD')[0]?.cmnCdNm && (
                  <div className="select">
                    <label>
                      {empInfoCode.filter((o) => o.cmnCd === 'PRDN_PROC_CD')[0]?.cmnCdNm}
                    </label>
                    <ComboBox
                      options={prdnProcCode}
                      defaultValue={prdnProcCd}
                      className="PRDN_PROC_CD"
                      isWrap={false}
                      onChange={(value) => handleChangeProc(value)}
                      placeholder={String(t('com.label.전체', '전체'))}
                    />
                  </div>
                )}
                {/* 법인 */}
                {empInfoCode.filter((o) => o.cmnCd === 'ELM_COP_CD')[0]?.cmnCdNm && (
                  <div className="select">
                    <label>{empInfoCode.filter((o) => o.cmnCd === 'ELM_COP_CD')[0]?.cmnCdNm}</label>
                    <ComboBox
                      options={elmCopCode}
                      defaultValue={elmCopCd}
                      className="ELM_COP_CD"
                      isWrap={false}
                      onChange={(value) => handleChangeElmCop(value)}
                      placeholder={String(t('com.label.전체', '전체'))}
                    />
                  </div>
                )}

                {/* MP분류 */}
                {empInfoCode.filter((o) => o.cmnCd === 'MP_CLSF_TYPE')[0]?.cmnCdNm && (
                  <div className="select">
                    <label>
                      {empInfoCode.filter((o) => o.cmnCd === 'MP_CLSF_TYPE')[0]?.cmnCdNm}
                    </label>
                    <ComboBox
                      options={mpClsfTypeCode}
                      defaultValue={mpClsfType}
                      className="MP_CLSF_TYPE"
                      isWrap={false}
                      onChange={(value) => handleChangeMpClsfType(value)}
                      placeholder={String(t('com.label.전체', '전체'))}
                    />
                  </div>
                )}
              </>
            )}
            {hasFaRole &&
              (empInfoCode || []).filter((o) => o.cmnCd === 'FA_ELM_PRJ_ID')[0]?.cmnCdNm && (
                <div className="select">
                  <label>
                    {empInfoCode.filter((o) => o.cmnCd === 'FA_ELM_PRJ_ID')[0]?.cmnCdNm}
                  </label>
                  <ComboBox
                    placeholder={String(t('com.label.선택', '선택'))}
                    options={optionsPrjMaster}
                    defaultValue={elmPrjId}
                    isWrap={false}
                    onChange={(value) => handleChangeElmPrjId(value)}
                  />
                </div>
              )}

            {/*{empInfoCode.map((o) => {*/}
            {/*  const codes = async () => {*/}
            {/*    return await getCommonCodeNames(o.cmnCd || '');*/}
            {/*  };*/}
            {/*  return (*/}
            {/*    // eslint-disable-next-line react/jsx-key*/}
            {/*    <div className="select">*/}
            {/*      <label>{o.cmnCdNm}</label>*/}
            {/*      <ComboBox*/}
            {/*        options={codes || []}*/}
            {/*        defaultValue={o.cmnCd}*/}
            {/*        className={o.cmnCd}*/}
            {/*        isWrap={false}*/}
            {/*        onChange={(value) => handleChangeTMZ(value)}*/}
            {/*      />*/}
            {/*    </div>*/}
            {/*  );*/}
            {/*})}*/}
            {empInfoCode.length > 0 && (
              <AvatarCon>
                <LayerBtnGroup>
                  <Button className="cancel" onClick={() => setOpenSettingMenu(false)}>
                    취소
                  </Button>
                  <Button className="apply" onClick={handleSettingApply}>
                    저장
                  </Button>
                </LayerBtnGroup>
              </AvatarCon>
            )}
          </div>
        </div>
        {userId && (
          <Avatar>
            <AvatarDiv className="iconInvers xsm" onClick={handleOpenAvatarMenu}>
              <div className="avatar"></div>
              <div className="name">{empNm || userId}</div>
            </AvatarDiv>
            <div className="AvatarBox" css={AvatarMenu.div(isOpenAvatarMenu)}>
              <AvatarDiv className="iconInvers lg" onClick={handleMoveUserEditPage}>
                <div className="avatar"></div>
                <div className="name">
                  {/*<span>홍길동 책임</span> OOO센터 OOOO팀{' '}*/}
                  <span>
                    {empNm} {jtiNm}
                  </span>{' '}
                  {deptNm}
                </div>
                {/* <AvatarCount className="count">9</AvatarCount> */}
                {/* <AvatarBadge className="badge confirm" /> */}
              </AvatarDiv>
              <CloseBtn onClick={() => setOpenAvatarMenu(false)} />
              <AvatarCon>
                <LayerBtnGroup>
                  <Button className="logout" onClick={handleLogout}>
                    {t('com.label.로그아웃', '로그아웃')}
                  </Button>
                </LayerBtnGroup>
              </AvatarCon>
            </div>
          </Avatar>
        )}
      </div>
    </div>
  );
};

const st = {
  root: (server: boolean, env?: string) => css`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 52px;
    z-index: 999;
    background: ${env === 'stg' ? '#F0DEFB' : server ? '#FFE56A' : '#F7F9F8'};
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-right: 22px;
    border-bottom: 1px solid ${CommonBorder.Basic};
  `,
  inner: css`
    height: 100%;
    display: flex;
    align-items: center;
    padding-left: 52px;
  `,
  right: css`
    height: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
  `,
  btn: css`
    min-width: 24px;
    padding: 0 !important;
    &:before {
      content: '';
      clear: both;
      display: inline-block;
      width: 24px;
      height: 24px;
      background: ${CommonText.Light};
      -webkit-mask: ${SVGIcon.IcoSetting};
      mask-image: ${SVGIcon.IcoSetting};
      mask-size: 24px;
    }
  `,
};

export default React.memo(HeadMenuBar);
